import type { ForecastResult } from '../types/wasm';
import { Tooltip } from './Tooltip';

interface SimplifiedForecastResultsProps {
  forecastResult: ForecastResult;
  forecastDates: string[];
  historicalData: Array<{ date: string; value: number }>;
  modelType: string;
}

export function SimplifiedForecastResults({
  forecastResult,
  forecastDates,
  historicalData,
  modelType,
}: SimplifiedForecastResultsProps) {
  const displayForecasts = forecastResult.forecasts;
  const firstForecast = displayForecasts[0];
  const lastForecast = displayForecasts[displayForecasts.length - 1];
  const trendPercent = Number.isFinite(firstForecast) && firstForecast !== 0
    ? ((lastForecast - firstForecast) / Math.abs(firstForecast)) * 100
    : 0;
  const trendDirection = trendPercent > 5 ? 'increasing' : trendPercent < -5 ? 'decreasing' : 'stable';
  const averageForecast = displayForecasts.length > 0
    ? displayForecasts.reduce((sum, value) => sum + value, 0) / displayForecasts.length
    : 0;
  const intervalWidth = forecastResult.upper_bounds.length > 0
    ? forecastResult.upper_bounds.reduce((sum, upper, index) => sum + Math.abs(upper - forecastResult.lower_bounds[index]), 0) / forecastResult.upper_bounds.length
    : 0;

  // Quality assessment
  const { mape } = forecastResult.diagnostics;
  const quality = mape < 10 ? 'Excellent' : mape < 20 ? 'Good' : mape < 30 ? 'Fair' : 'Needs Improvement';
  const qualityColor = mape < 10 ? 'green' : mape < 20 ? 'blue' : mape < 30 ? 'yellow' : 'red';
  const qualityClasses = {
    green: 'border-emerald-200 bg-emerald-50/70 text-emerald-700',
    blue: 'border-blue-200 bg-blue-50/70 text-blue-700',
    yellow: 'border-amber-200 bg-amber-50/70 text-amber-700',
    red: 'border-rose-200 bg-rose-50/70 text-rose-700',
  }[qualityColor];
  const trendClasses = trendDirection === 'increasing'
    ? 'border-emerald-200 bg-emerald-50/70 text-emerald-700'
    : trendDirection === 'decreasing'
    ? 'border-orange-200 bg-orange-50/70 text-orange-700'
    : 'border-slate-200 bg-slate-50/80 text-slate-700';
  const formatNumber = (value: number) => Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'N/A';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-150 bg-white">
        <h2 className="text-lg font-bold text-gray-900">Forecast Results</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Results are shown on the original data scale, including log and square-root model runs.
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`p-5 rounded-xl border ${qualityClasses}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center space-x-1">
                <span className="text-[11px] font-black uppercase tracking-wider text-gray-500">Forecast Quality</span>
              <Tooltip content="Quality is based on MAPE (Mean Absolute Percentage Error). Lower error = better accuracy. Excellent: <10%, Good: 10-20%, Fair: 20-30%, Needs Improvement: >30%">
                <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </Tooltip>
            </div>
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-white/70 border border-white rounded-full px-2 py-0.5">
                MAPE
            </span>
          </div>
            <div className="text-2xl font-black text-current">{quality}</div>
            <div className="text-xs text-gray-600 mt-1">{mape.toFixed(1)}% average error</div>
        </div>

          <div className={`p-5 rounded-xl border ${trendClasses}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center space-x-1">
                <span className="text-[11px] font-black uppercase tracking-wider text-gray-500">Trend</span>
              <Tooltip content="Overall direction of your forecast. Increasing: values going up, Decreasing: values going down, Stable: relatively flat. Calculated from first to last forecasted value.">
                <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </Tooltip>
            </div>
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-white/70 border border-white rounded-full px-2 py-0.5">
                {forecastDates.length} periods
            </span>
          </div>
            <div className="text-2xl font-black capitalize text-current">{trendDirection}</div>
            <div className="text-xs text-gray-600 mt-1">
              {trendPercent > 0 ? '+' : ''}{trendPercent.toFixed(1)}% from first to last forecast
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="p-4 bg-slate-50/70 rounded-xl border border-gray-200/80">
          <Tooltip content="The number of historical data points used to train the forecasting model. More data generally leads to better predictions.">
            <div className="cursor-help">
                <div className="text-xl font-black text-gray-900">{historicalData.length}</div>
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1 flex items-center space-x-1">
                <span>Historical Data Points</span>
                <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </Tooltip>
        </div>
          <div className="p-4 bg-slate-50/70 rounded-xl border border-gray-200/80">
          <Tooltip content="How many future time periods this forecast predicts. Also called the 'forecast horizon' or 'forecast window'.">
            <div className="cursor-help">
                <div className="text-xl font-black text-gray-900">{forecastDates.length}</div>
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1 flex items-center space-x-1">
                <span>Periods Forecasted</span>
                <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </Tooltip>
        </div>
          <div className="p-4 bg-slate-50/70 rounded-xl border border-gray-200/80">
          <Tooltip content={`${modelType === 'ARIMA' ? 'ARIMA (AutoRegressive Integrated Moving Average) - Best for data with trends and seasonality' : modelType === 'Exponential Smoothing' ? 'Exponential Smoothing - Best for data with consistent patterns and seasonal variations' : 'The statistical method used to generate this forecast'}`}>
            <div className="cursor-help">
                <div className="text-lg font-black text-gray-900 leading-tight">{modelType}</div>
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1 flex items-center space-x-1">
                <span>Model Type</span>
                <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </Tooltip>
        </div>
          <div className="p-4 bg-slate-50/70 rounded-xl border border-gray-200/80">
            <div className="text-xl font-black text-gray-900">{formatNumber(averageForecast)}</div>
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1">Average Forecast</div>
          </div>
          <div className="p-4 bg-slate-50/70 rounded-xl border border-gray-200/80">
            <div className="text-xl font-black text-gray-900">{formatNumber(intervalWidth)}</div>
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1">Avg. Interval Width</div>
          </div>
        </div>

        <div className="p-4 bg-blue-50/60 border border-blue-150 rounded-xl">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-black text-sm flex-shrink-0">i</div>
          <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-2 text-sm">Key Insights</h3>
            <ul className="space-y-1 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Your forecast shows <strong>{trendDirection}</strong> trend with{' '}
                  <strong>{quality.toLowerCase()}</strong> accuracy
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  {mape < 20
                    ? 'Suitable for strategic planning and decision-making'
                    : mape < 30
                    ? 'Consider improving data quality or trying different model parameters'
                    : 'Review your data for missing values, outliers, or consider collecting more data'
                  }
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Best for: {forecastDates.length <= 12 ? 'Short-term planning (1-12 periods)' : 'Medium-term planning (12+ periods)'}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
